/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type UseFormReturn, type FieldValues, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormField, FormItem, FormLabel, FormMessage, useFormField } from '@/components/forms/form';
import { Input } from '@/components/forms/input';
import { NativeSelect, NativeSelectOption } from '@/components/forms/native-select';
import AddressSuggestionDropdown, { type AddressSuggestion } from '@/components/address-suggestion-dropdown';
import { MIN_INPUT_LENGTH, useAutocompleteSuggestions } from '@/hooks/use-autocomplete-suggestions';
import { useExperianAddressSuggestions } from '@/hooks/use-experian-address-suggestions';
import { processAddressSuggestion } from '@/lib/address/address-suggestions';
import { UITarget } from '@/targets/ui-target';
import { formatPhoneInput, stripNonDigits, stripCountryCode } from '@/lib/address/phone-utils';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { NAME_MAX_LENGTH } from '@/lib/checkout/schemas';

/**
 * Control slot for address inputs: sets `aria-describedby` only when a validation message exists.
 * Stock `FormControl` always references `formDescriptionId` even when no `FormDescription` is rendered,
 * which breaks strict a11y checks. Use this (or {@link AddressFormFields}) for checkout address UIs.
 */
export function AddressFormControl({ ...props }: ComponentProps<typeof Slot>) {
    const { error, formItemId, formMessageId } = useFormField();

    return (
        <Slot
            data-form-control=""
            data-slot="form-control"
            id={formItemId}
            aria-describedby={error ? formMessageId : undefined}
            aria-invalid={!!error}
            {...props}
        />
    );
}

/**
 * Base address field names that the form must support
 */
export interface AddressFields {
    firstName: string;
    lastName: string;
    address1: string;
    address2: string;
    city: string;
    stateCode: string;
    postalCode: string;
    phoneCountryCode?: string;
    phone?: string;
}

/**
 * Props for the AddressFormFields component
 */
export interface AddressFormFieldsProps<TFormValues extends FieldValues> {
    /** React Hook Form instance */
    form: UseFormReturn<TFormValues>;
    /**
     * Prefix for field names (e.g., 'billing' for billing address fields).
     * When provided, field names become 'billingFirstName', 'billingAddress1', etc.
     * When empty, field names are 'firstName', 'address1', etc.
     */
    fieldPrefix?: string;
    /** Whether to show the phone field (default: true) */
    showPhone?: boolean;
    /** Whether to auto-focus a field when the form is shown (default: false) */
    autoFocus?: boolean;
    /** Which field to focus when autoFocus is true: 'firstName' or 'address1' (default: 'address1') */
    autoFocusField?: 'firstName' | 'address1';
    /** Country code for address autocomplete (default: 'US') */
    countryCode?: string;
    /** Custom class name for the container */
    className?: string;
    /** When true, hide labels and use placeholders only (e.g. for billing address UX) */
    labelsAsPlaceholders?: boolean;
    /** When true, show Country dropdown (e.g. for billing address) */
    showCountry?: boolean;
    /** When true, phone is required (append * to label / placeholder when using placeholder labels) */
    phoneRequired?: boolean;
}

/**
 * Shared address form fields component with Google Maps / Experian autocomplete integration.
 *
 * When `features.experianAddressApi.enabled` is set in config, uses Experian Aperture API
 * for address autocomplete; otherwise falls back to Google Maps Places API.
 *
 * @example
 * ```tsx
 * // For shipping address (no prefix)
 * <AddressFormFields form={form} showPhone={true} autoFocus={isEditing} />
 *
 * // For billing address (with prefix)
 * <AddressFormFields form={form} fieldPrefix="billing" showPhone={false} />
 * ```
 */
export function AddressFormFields<TFormValues extends FieldValues>({
    form,
    fieldPrefix = '',
    showPhone = true,
    autoFocus = false,
    autoFocusField = 'address1',
    countryCode = 'US',
    className,
    labelsAsPlaceholders = false,
    showCountry = false,
    phoneRequired = false,
}: AddressFormFieldsProps<TFormValues>) {
    const { t } = useTranslation('checkout');
    const { t: tSuggestions } = useTranslation('addressSuggestionDropdown');
    const { t: tCountries } = useTranslation('countries');
    const config = useConfig();
    const experianConfig = (
        config.features as { experianAddressApi?: { enabled?: boolean; countryCode?: string } } | undefined
    )?.experianAddressApi;
    const useExperian = experianConfig?.enabled ?? false;

    // Address autocomplete state
    const [addressInput, setAddressInput] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);

    // SSF-672: ref for autofill sync (hook registered below after getFieldName)
    const containerRef = useRef<HTMLDivElement>(null);

    // Experian Aperture API (when enabled)
    const experianCountryCode = experianConfig?.countryCode ?? countryCode;
    const experian = useExperianAddressSuggestions(addressInput, useExperian, experianCountryCode);

    // Google Maps Places API (when Experian not enabled)
    const google = useAutocompleteSuggestions({
        inputString: addressInput,
        countryCode,
    });

    const addressSuggestions = useExperian ? experian.suggestions : google.suggestions;
    const isLoadingSuggestions = useExperian ? experian.isLoading : google.isLoading;
    const tooManyMatches = useExperian ? experian.tooManyMatches : false;

    /**
     * Helper to construct field names with optional prefix
     * e.g., with prefix 'billing': 'firstName' becomes 'billingFirstName'
     */
    const getFieldName = (baseName: string): Path<TFormValues> => {
        if (!fieldPrefix) {
            return baseName as Path<TFormValues>;
        }
        // Capitalize first letter of baseName when prefixing
        const capitalizedBaseName = baseName.charAt(0).toUpperCase() + baseName.slice(1);
        return `${fieldPrefix}${capitalizedBaseName}` as Path<TFormValues>;
    };

    /**
     * Helper to construct autoComplete attribute with proper section prefix
     * e.g., with prefix 'billing': 'given-name' becomes 'billing given-name'
     * e.g., with prefix '' (shipping): 'given-name' becomes 'shipping given-name'
     */
    const getAutoComplete = (autoCompleteValue: string): string => {
        const section = fieldPrefix || 'shipping';
        return `${section} ${autoCompleteValue}`;
    };

    // SSF-672: Detect browser autofill (batch-fills multiple fields) and sync DOM → RHF.
    // Browser autofill sets DOM values directly and may not trigger React's synthetic
    // onChange reliably (especially <select>). When 2+ fields change within 80ms we
    // treat it as autofill and push DOM values into RHF via setValue.
    const autofillFields = useMemo(
        () =>
            [
                getFieldName('firstName'),
                getFieldName('lastName'),
                getFieldName('address1'),
                getFieldName('address2'),
                getFieldName('city'),
                getFieldName('stateCode'),
                getFieldName('postalCode'),
                getFieldName('phone'),
            ] as Path<TFormValues>[],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fieldPrefix]
    );

    const syncAutofillFields = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;
        for (const fieldName of autofillFields) {
            const el = container.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${fieldName}"]`);
            if (!el) continue;
            const domValue = el.value;
            const rhfValue = form.getValues(fieldName);
            if (domValue && domValue !== rhfValue) {
                form.setValue(fieldName, domValue as TFormValues[Path<TFormValues>], {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            }
        }
    }, [autofillFields, form]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let changeCount = 0;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const handleChange = (e: Event) => {
            if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement)) return;
            changeCount++;
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                if (changeCount >= 2) syncAutofillFields();
                changeCount = 0;
            }, 80);
        };

        container.addEventListener('change', handleChange, true);
        container.addEventListener('input', handleChange, true);
        return () => {
            container.removeEventListener('change', handleChange, true);
            container.removeEventListener('input', handleChange, true);
            if (timer) clearTimeout(timer);
        };
    }, [syncAutofillFields]);

    // When showCountry is true (e.g. billing), watch country so we can show State/Province and Zip Code/Postal Code labels
    const watchedCountry = showCountry ? form.watch(getFieldName('countryCode')) : undefined;
    const effectiveCountry = showCountry ? watchedCountry || countryCode || 'US' : countryCode || 'US';
    const useProvinceLabel = effectiveCountry === 'CA';
    const useZipLabel = effectiveCountry === 'US';

    const statesObj = useMemo(() => {
        return effectiveCountry && (effectiveCountry === 'US' || effectiveCountry === 'CA')
            ? (tCountries(`${effectiveCountry}.states`, { returnObjects: true }) as Record<string, string>)
            : null;
    }, [effectiveCountry, tCountries]);

    const handleSelectSuggestion = async (suggestion: AddressSuggestion) => {
        setShowSuggestions(false);

        if (useExperian) {
            const key = suggestion.global_address_key ?? suggestion.place_id;
            if (!key) return;
            const addressFields = await experian.fetchAddressFormat(key);
            if (!addressFields) return;

            const formFields = [
                { name: getFieldName('address1'), value: addressFields.address1 },
                { name: getFieldName('address2'), value: addressFields.address2 ?? '' },
                { name: getFieldName('city'), value: addressFields.city ?? '' },
                { name: getFieldName('stateCode'), value: addressFields.stateCode ?? '' },
                { name: getFieldName('postalCode'), value: addressFields.postalCode ?? '' },
            ] as const;

            for (const { name, value } of formFields) {
                form.setValue(name, value as TFormValues[Path<TFormValues>], {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            }

            experian.resetSession();
        } else {
            const addressFields = await processAddressSuggestion(suggestion);
            form.setValue(getFieldName('address1'), addressFields.address1 as TFormValues[Path<TFormValues>]);
            if (addressFields.city) {
                form.setValue(getFieldName('city'), addressFields.city as TFormValues[Path<TFormValues>]);
            }
            if (addressFields.stateCode) {
                form.setValue(getFieldName('stateCode'), addressFields.stateCode as TFormValues[Path<TFormValues>]);
            }
            if (addressFields.postalCode) {
                form.setValue(getFieldName('postalCode'), addressFields.postalCode as TFormValues[Path<TFormValues>]);
            }
            google.resetSession();
        }

        setAddressInput('');
    };

    const handleCloseSuggestions = () => {
        setShowSuggestions(false);
    };

    const handleAddressInputChange = (e: ChangeEvent<HTMLInputElement>, fieldOnChange: (value: string) => void) => {
        const value = e.target.value;
        fieldOnChange(value);
        setAddressInput(value);
        if (value.length >= MIN_INPUT_LENGTH) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    /**
     * Renders the address autocomplete dropdown with target extensibility.
     * Uses different targetIds for shipping vs billing addresses to allow
     * extension developers to customize each independently.
     *
     * The dropdown is rendered whenever `showSuggestions` is true (i.e. the shopper
     * has typed past MIN_INPUT_LENGTH). The component itself handles loading, hint,
     * and "No matches" sub-states.
     */
    const renderAddressAutocomplete = (): React.ReactNode => {
        if (!showSuggestions) {
            return null;
        }

        const hintMessage: string | undefined = tooManyMatches
            ? (tSuggestions as (key: string) => string)('tooManyMatches')
            : undefined;

        const dropdown = (
            <AddressSuggestionDropdown
                suggestions={addressSuggestions}
                isVisible={showSuggestions}
                isLoading={isLoadingSuggestions}
                onClose={handleCloseSuggestions}
                onSelectSuggestion={(suggestion) => void handleSelectSuggestion(suggestion)}
                hintMessage={hintMessage}
                showGoogleAttribution={!useExperian}
            />
        );

        if (fieldPrefix === 'billing') {
            return (
                <div>
                    <UITarget targetId="sfcc.checkout.payment.billingAddress.autocomplete">{dropdown}</UITarget>
                </div>
            );
        }

        return (
            <div>
                <UITarget targetId="sfcc.checkout.shippingAddress.autocomplete">{dropdown}</UITarget>
            </div>
        );
    };

    return (
        <div ref={containerRef} className={className}>
            {/* First Name and Last Name Row */}
            {/* `items-start` keeps both inputs top-aligned when one cell renders a validation
                message and the other doesn't. Without it, the default `align-items: stretch`
                stretches the shorter FormItem to the row height, which pushes its input down
                the second the adjacent field starts (or stops) showing an error. */}
            <div className="grid grid-cols-2 gap-2 mb-4 items-start">
                <FormField
                    control={form.control}
                    name={getFieldName('firstName')}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                {t('addressForm.firstNameLabel')}*
                            </FormLabel>
                            <AddressFormControl>
                                <Input
                                    placeholder={
                                        labelsAsPlaceholders
                                            ? `${t('addressForm.firstNameLabel')}*`
                                            : t('addressForm.firstNamePlaceholder')
                                    }
                                    autoComplete={getAutoComplete('given-name')}
                                    autoFocus={autoFocus && autoFocusField === 'firstName'}
                                    maxLength={NAME_MAX_LENGTH}
                                    {...field}
                                />
                            </AddressFormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name={getFieldName('lastName')}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                {t('addressForm.lastNameLabel')}*
                            </FormLabel>
                            <AddressFormControl>
                                <Input
                                    placeholder={
                                        labelsAsPlaceholders
                                            ? `${t('addressForm.lastNameLabel')}*`
                                            : t('addressForm.lastNamePlaceholder')
                                    }
                                    autoComplete={getAutoComplete('family-name')}
                                    maxLength={NAME_MAX_LENGTH}
                                    {...field}
                                />
                            </AddressFormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            {/* Address Line 1 with Autocomplete */}
            <div className="mb-4">
                <FormField
                    control={form.control}
                    name={getFieldName('address1')}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                {t('addressForm.addressLabel')}*
                            </FormLabel>
                            <div className="relative">
                                <AddressFormControl>
                                    <Input
                                        placeholder={
                                            labelsAsPlaceholders
                                                ? `${t('addressForm.addressLabel')}*`
                                                : t('addressForm.addressPlaceholder')
                                        }
                                        autoComplete={showSuggestions ? 'off' : getAutoComplete('address-line1')}
                                        autoFocus={autoFocus && autoFocusField === 'address1'}
                                        {...field}
                                        onChange={(e) => handleAddressInputChange(e, field.onChange)}
                                    />
                                </AddressFormControl>
                                {renderAddressAutocomplete()}
                            </div>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            {/* Address Line 2 */}
            <div className="mb-4">
                <FormField
                    control={form.control}
                    name={getFieldName('address2')}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                {t('addressForm.address2Label')}
                            </FormLabel>
                            <AddressFormControl>
                                <Input
                                    placeholder={
                                        labelsAsPlaceholders
                                            ? t('addressForm.address2Label')
                                            : t('addressForm.address2Placeholder')
                                    }
                                    autoComplete={getAutoComplete('address-line2')}
                                    {...field}
                                />
                            </AddressFormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            {/* City and State Row */}
            <div className="grid grid-cols-2 gap-2 mb-4 items-start">
                <FormField
                    control={form.control}
                    name={getFieldName('city')}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                {t('addressForm.cityLabel')}*
                            </FormLabel>
                            <AddressFormControl>
                                <Input
                                    placeholder={
                                        labelsAsPlaceholders
                                            ? `${t('addressForm.cityLabel')}*`
                                            : t('addressForm.cityPlaceholder')
                                    }
                                    autoComplete={getAutoComplete('address-level2')}
                                    {...field}
                                />
                            </AddressFormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name={getFieldName('stateCode')}
                    render={({ field }) => {
                        const stateOptions = statesObj ? Object.entries(statesObj) : [];
                        const stateLabel = useProvinceLabel
                            ? t('addressForm.provinceLabel')
                            : t('addressForm.stateLabel');
                        const statePlaceholder = useProvinceLabel
                            ? t('addressForm.provincePlaceholder')
                            : t('addressForm.statePlaceholder');

                        return (
                            <FormItem className="[&_[data-slot=native-select-wrapper]]:w-full">
                                <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                    {stateLabel}*
                                </FormLabel>
                                <AddressFormControl>
                                    {stateOptions.length > 0 ? (
                                        <NativeSelect
                                            autoComplete={getAutoComplete('address-level1')}
                                            className={!field.value ? 'text-muted-foreground' : ''}
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value)}>
                                            <NativeSelectOption value="" className="text-muted-foreground">
                                                {labelsAsPlaceholders ? `${stateLabel}*` : statePlaceholder}
                                            </NativeSelectOption>
                                            {stateOptions.map(([code, name]) => (
                                                <NativeSelectOption key={code} value={code}>
                                                    {name}
                                                </NativeSelectOption>
                                            ))}
                                        </NativeSelect>
                                    ) : (
                                        <Input
                                            placeholder={labelsAsPlaceholders ? `${stateLabel}*` : statePlaceholder}
                                            autoComplete={getAutoComplete('address-level1')}
                                            {...field}
                                        />
                                    )}
                                </AddressFormControl>
                                <FormMessage />
                            </FormItem>
                        );
                    }}
                />
            </div>

            {/* Zip Code / Postal Code and Country Row */}
            <div className={`grid grid-cols-2 gap-2 items-start ${showPhone ? 'mb-4' : ''}`}>
                <FormField
                    control={form.control}
                    name={getFieldName('postalCode')}
                    render={({ field }) => {
                        const postalLabel = useZipLabel ? t('addressForm.zipLabel') : t('addressForm.postalCodeLabel');
                        const postalPlaceholder = useZipLabel
                            ? t('addressForm.zipPlaceholder')
                            : t('addressForm.postalCodePlaceholder');
                        return (
                            <FormItem>
                                <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                    {postalLabel}*
                                </FormLabel>
                                <AddressFormControl>
                                    <Input
                                        placeholder={labelsAsPlaceholders ? `${postalLabel}*` : postalPlaceholder}
                                        autoComplete={getAutoComplete('postal-code')}
                                        {...field}
                                    />
                                </AddressFormControl>
                                <FormMessage />
                            </FormItem>
                        );
                    }}
                />

                {showCountry ? (
                    <FormField
                        control={form.control}
                        name={getFieldName('countryCode')}
                        render={({ field }) => {
                            const code = (field.value as string) || 'US';
                            return (
                                <FormItem>
                                    <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                        {t('addressForm.countryLabel')}*
                                    </FormLabel>
                                    <AddressFormControl>
                                        <Input
                                            type="text"
                                            name={field.name}
                                            value={tCountries(`${code}.name` as `US.name`)}
                                            disabled
                                            autoComplete={getAutoComplete('country')}
                                            aria-label={t('addressForm.countryLabel')}
                                            className="text-foreground bg-background"
                                        />
                                    </AddressFormControl>
                                    <FormMessage />
                                </FormItem>
                            );
                        }}
                    />
                ) : (
                    <FormItem>
                        <FormLabel>{t('addressForm.countryLabel')}*</FormLabel>
                        <Input
                            value={tCountries(`${countryCode}.name` as `US.name`)}
                            disabled
                            className="text-foreground bg-background"
                        />
                    </FormItem>
                )}
            </div>

            {/* Phone Field (optional) */}
            {showPhone && (
                <div className="flex items-start gap-2">
                    <FormField
                        control={form.control}
                        name={getFieldName('phoneCountryCode')}
                        render={({ field }) => <input type="hidden" {...field} value="+1" disabled />}
                    />
                    <FormField
                        control={form.control}
                        name={getFieldName('phone')}
                        render={({ field }) => (
                            <FormItem className="flex-1">
                                <FormLabel className={labelsAsPlaceholders ? 'sr-only' : undefined}>
                                    {t('addressForm.phoneLabel')}
                                    {phoneRequired ? '*' : ''}
                                </FormLabel>
                                <AddressFormControl>
                                    <Input
                                        type="tel"
                                        inputMode="numeric"
                                        placeholder={
                                            labelsAsPlaceholders && phoneRequired
                                                ? `${t('addressForm.phoneLabel')}*`
                                                : t('addressForm.phonePlaceholder')
                                        }
                                        autoComplete="tel-national"
                                        maxLength={14}
                                        {...field}
                                        value={stripCountryCode(field.value || '')}
                                        onChange={(e) => {
                                            field.onChange(stripNonDigits(e.target.value).slice(0, 10));
                                        }}
                                        onBlur={(e) => {
                                            field.onBlur();
                                            field.onChange(formatPhoneInput(e.target.value));
                                        }}
                                        onFocus={(e) => {
                                            const digits = stripNonDigits(e.target.value);
                                            if (digits !== e.target.value) field.onChange(digits);
                                        }}
                                    />
                                </AddressFormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
            )}
        </div>
    );
}

export default AddressFormFields;
